
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function EmergencyLightingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Emergency Lighting Installations',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional emergency lighting installation and testing services in Fresno CA for commercial buildings to ensure code compliance and life safety',
    url: `${siteUrl}/services/commercial-specialist/emergency-lighting`,
  };

  const features = [
    {
      icon: 'ri-flashlight-line',
      title: 'Emergency Light Installation',
      description: 'Battery-backup emergency lighting for safe egress during power outages',
    },
    {
      icon: 'ri-door-open-line',
      title: 'Exit Sign Installation',
      description: 'Illuminated exit signs meeting California fire code requirements',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Monthly Testing Service',
      description: 'Required monthly emergency lighting testing and documentation',
    },
    {
      icon: 'ri-battery-charge-line',
      title: 'Battery Replacement',
      description: 'Emergency light battery testing and replacement services',
    },
    {
      icon: 'ri-file-list-line',
      title: 'Compliance Documentation',
      description: 'Detailed testing records for fire marshal and insurance inspections',
    },
    {
      icon: 'ri-tools-line',
      title: 'Emergency Light Repairs',
      description: 'Fast repairs to keep emergency lighting systems operational',
    },
  ];

  return (
    <>
      <SEOHead
        title="Emergency Lighting Installation Fresno | Exit Sign Electrician CA"
        description="Professional emergency lighting installation and testing in Fresno CA. Exit signs, battery backup lights, monthly testing, and code compliance. Licensed electricians."
        canonical="/services/commercial-specialist/emergency-lighting"
        keywords="emergency lighting Fresno, exit sign installation, emergency light testing, fire code compliance"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Emergency Lighting Installations — Fresno, CA"
        heroSubtitle="Code-compliant emergency lighting and exit signs for commercial buildings in Fresno"
        heroImage="https://public.readdy.ai/ai/img_res/9b15bcb604d90565d6ec468cd886336d.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Professional%20electrician%20installing%20emergency%20exit%20sign%20with%20battery%20backup%20in%20commercial%20building%20with%20organized%20wiring%20and%20proper%20mounting&width=700&height=600&seq=emergency-light-fresno-001&orientation=portrait"
        fresnoContent="Ensuring life safety for Fresno commercial buildings with professional emergency lighting solutions. From office buildings to retail centers, restaurants to warehouses throughout Fresno County, we install and maintain emergency lighting systems that meet California fire codes and ensure safe egress during power outages. Our licensed electricians understand the critical importance of reliable emergency lighting—it's required by law and essential for protecting building occupants. We provide complete installation, monthly testing services, and compliance documentation that satisfies fire marshal inspections and insurance requirements for Fresno commercial properties."
        features={features}
        faqs={[
          {
            question: 'What emergency lighting is required for Fresno commercial buildings?',
            answer:
              'California fire codes require emergency lighting for all commercial buildings in Fresno including illuminated exit signs at all exits and along egress paths, emergency lighting that provides adequate illumination for safe evacuation, battery backup systems that operate for minimum 90 minutes during power outages, emergency lights in stairwells and corridors, exit path marking and directional signs, and emergency lighting in assembly occupancies and high-occupancy buildings. Requirements vary based on building type, occupancy classification, and square footage. We evaluate your Fresno commercial property and design emergency lighting systems that meet all California fire code requirements.',
          },
          {
            question: 'How often must emergency lighting be tested in Fresno?',
            answer:
              'California fire codes require monthly emergency lighting testing for all commercial buildings in Fresno. Monthly tests verify that emergency lights and exit signs illuminate properly when normal power is interrupted. Annual tests require a full 90-minute battery discharge test to ensure batteries maintain adequate charge. All testing must be documented with dates, results, and any deficiencies found. Fire marshals and insurance inspectors review these records during inspections. We provide monthly testing services for Fresno commercial properties, maintaining detailed compliance records and addressing any issues immediately to keep your building code-compliant.',
          },
          {
            question: 'How much does emergency lighting installation cost in Fresno?',
            answer:
              'Emergency lighting costs in Fresno vary based on building size and code requirements. Basic emergency light fixtures with battery backup cost $150-$400 each installed. LED exit signs range from $100-$300 each installed. A typical small office building (2,000-5,000 sq ft) requires $1,500-$4,000 for complete emergency lighting. Medium commercial buildings (5,000-20,000 sq ft) average $4,000-$12,000. Large facilities may require $12,000-$50,000+ depending on layout and occupancy requirements. Monthly testing service typically costs $100-$300 per visit depending on building size. We provide detailed estimates after evaluating your Fresno commercial property and code requirements.',
          },
          {
            question: 'Can you provide monthly emergency lighting testing in Fresno?',
            answer:
              'Yes, we provide monthly emergency lighting testing services for Fresno commercial properties. Our licensed electricians visit your building monthly to test all emergency lights and exit signs, verify proper operation and illumination, document test results with dates and findings, replace batteries as needed, repair any deficiencies immediately, and maintain compliance records for fire marshal inspections. Monthly testing service ensures your emergency lighting is always operational and your building remains code-compliant. We serve offices, retail stores, restaurants, warehouses, and all commercial properties throughout Fresno with reliable testing programs.',
          },
          {
            question: 'What happens if emergency lighting fails inspection in Fresno?',
            answer:
              'Failed emergency lighting can result in serious consequences for Fresno commercial properties including fire marshal violations and fines, failed building inspections, insurance policy issues or cancellations, inability to obtain or renew occupancy permits, liability concerns if emergencies occur, and potential building closure until corrected. We respond immediately to emergency lighting failures, providing same-day repairs when possible. Our monthly testing service catches problems before they become violations. If your Fresno building has failed emergency lighting inspection, we can quickly assess the situation, make necessary repairs or replacements, and coordinate re-inspection to bring your property into compliance.',
          },
          {
            question: 'Are you licensed for emergency lighting work in Fresno?',
            answer:
              "Yes, we hold a California C-10 Electrical Contractor license required for all emergency lighting work in Fresno CA. Our electricians are trained in California fire codes, emergency lighting requirements, battery backup systems, and life safety electrical systems. We carry full commercial liability insurance and workers' compensation coverage. All installations are permitted through the City of Fresno Building Department when required and inspected for code compliance. We work closely with fire marshals and building inspectors to ensure emergency lighting systems meet all requirements. Our team provides professional emergency lighting solutions for commercial properties throughout Fresno.",
          },
        ]}
      />
    </>
  );
}
